/**
 * Accessory Request Service
 * Service สำหรับจัดการ API การขออุปกรณ์เสริม
 *
 * รองรับ 3 ประเภท:
 * - REQUEST_NEW: เบิกใหม่
 * - REPLACE: เปลี่ยน
 * - PURCHASE: ขอซื้อ
 */
import ApiWithLogging from '../utils/apiWithLogging';

// Enums
export type AccessoryRequestType = 'REQUEST_NEW' | 'REPLACE' | 'PURCHASE';
export type AccessoryCondition = 'DAMAGED' | 'LOST' | 'WORN_OUT';

// Icon types for request (used by components to render appropriate icons)
export type RequestIconType = 'plus' | 'refresh' | 'cart';

// Constants for UI
export const REQUEST_TYPES = [
  { value: 'REQUEST_NEW' as AccessoryRequestType, label: 'เบิกใหม่', iconType: 'plus' as RequestIconType, color: 'blue' },
  { value: 'REPLACE' as AccessoryRequestType, label: 'เปลี่ยน', iconType: 'refresh' as RequestIconType, color: 'orange' },
  { value: 'PURCHASE' as AccessoryRequestType, label: 'ขอซื้อ', iconType: 'cart' as RequestIconType, color: 'green' },
];

export const ACCESSORY_CONDITIONS = [
  { value: 'DAMAGED' as AccessoryCondition, label: 'ชำรุด' },
  { value: 'LOST' as AccessoryCondition, label: 'สูญหาย' },
  { value: 'WORN_OUT' as AccessoryCondition, label: 'สึกหรอ' },
];

// Types
export interface DeviceSimple {
  id: number;
  code: string | null;
  name: string | null;
  quantity: number | null;
  unit: string | null;
}

export interface UserSimple {
  id: number;
  username: string | null;
  full_name: string | null;
}

export interface AccessoryRequestCreate {
  request_type: AccessoryRequestType;
  device_id: number;
  quantity?: number;
  rental_item_id?: number | null;
  old_condition?: AccessoryCondition | null;  // Required for REPLACE
  reason: string;
  notes?: string | null;
}

export interface AccessoryRequestResponse {
  id: number;
  rental_id: number;
  rental_item_id: number | null;
  device_id: number;
  request_type: string;
  quantity: number;
  old_condition: string | null;
  reason: string;
  notes: string | null;
  request_date: string;
  requested_by_user_id: number;
  created_at: string;
  device: DeviceSimple | null;
  requested_by: UserSimple | null;
}

export interface AccessoryRequestList {
  items: AccessoryRequestResponse[];
  total: number;
}

// ========== Accessory Request Center Types ==========

export interface RentalForAccessoryItem {
  rental_id: number;
  rental_code: string;
  rental_status: string;
  company_id: number | null;
  company_name: string | null;
  site_id: number | null;
  site_name: string | null;
  item_count: number;
  accessory_request_count: number;
}

export interface RentalSearchForAccessoryResponse {
  items: RentalForAccessoryItem[];
  total: number;
}

export interface AccessoryItem {
  id: number;
  code: string | null;
  name: string;
  quantity: number;
  unit: string | null;
  minimum_quantity: number | null;
  device_type_id: number | null;
  device_type_name: string | null;
}

export interface AccessoryListResponse {
  items: AccessoryItem[];
  total: number;
}

export interface AllAccessoryRequestHistoryItem {
  id: number;
  request_date: string;
  request_type: string;
  quantity: number;
  old_condition: string | null;
  device_id: number;
  device_code: string | null;
  device_name: string | null;
  device_unit: string | null;
  rental_id: number;
  rental_code: string;
  rental_item_id: number | null;
  company_id: number | null;
  company_name: string | null;
  site_id: number | null;
  site_name: string | null;
  reason: string;
  notes: string | null;
  requested_by_user_id: number;
  requested_by_name: string | null;
  created_at: string;
}

export interface AllAccessoryRequestHistoryResponse {
  items: AllAccessoryRequestHistoryItem[];
  total: number;
  page: number;
  limit: number;
}

export interface AllHistoryFilters {
  page?: number;
  limit?: number;
  date_from?: string;
  date_to?: string;
  company_id?: number;
  request_type?: string;
}

// Service
const accessoryRequestService = {
  /**
   * ขออุปกรณ์เสริมสำหรับสัญญาเช่า
   */
  async createRequest(
    rentalId: number,
    data: AccessoryRequestCreate
  ): Promise<AccessoryRequestResponse> {
    const response = await ApiWithLogging.post<AccessoryRequestResponse>(
      `/accessory-requests/rentals/${rentalId}/request`,
      data
    );
    return response;
  },

  /**
   * ดึงประวัติการขออุปกรณ์เสริมของสัญญาเช่า
   */
  async getRentalHistory(
    rentalId: number,
    skip: number = 0,
    limit: number = 100
  ): Promise<AccessoryRequestList> {
    const response = await ApiWithLogging.get<AccessoryRequestList>(
      `/accessory-requests/rentals/${rentalId}/history`,
      { params: { skip, limit } }
    );
    return response;
  },

  // ========== Accessory Request Center Functions ==========

  /**
   * ค้นหาสัญญาเช่าสำหรับขออุปกรณ์เสริม (Accessory Request Center)
   */
  async searchRentalsForAccessory(
    query: string,
    limit: number = 10
  ): Promise<RentalSearchForAccessoryResponse> {
    const response = await ApiWithLogging.get<RentalSearchForAccessoryResponse>(
      '/accessory-requests/search-rentals',
      { params: { q: query, limit } }
    );
    return response;
  },

  /**
   * ดึงรายการอุปกรณ์เสริมทั้งหมด
   */
  async getAccessories(
    limit: number = 100
  ): Promise<AccessoryListResponse> {
    const response = await ApiWithLogging.get<AccessoryListResponse>(
      '/accessory-requests/accessories',
      { params: { limit } }
    );
    return response;
  },

  /**
   * ดึงประวัติการขออุปกรณ์เสริมทั้งระบบ (Accessory Request Center)
   */
  async getAllHistory(
    filters: AllHistoryFilters = {}
  ): Promise<AllAccessoryRequestHistoryResponse> {
    const params: Record<string, string | number | undefined> = {
      page: filters.page || 1,
      limit: filters.limit || 20,
    };

    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.company_id) params.company_id = filters.company_id;
    if (filters.request_type) params.request_type = filters.request_type;

    const response = await ApiWithLogging.get<AllAccessoryRequestHistoryResponse>(
      '/accessory-requests/all-history',
      { params }
    );
    return response;
  },

  /**
   * ดึงประเภทการขอ (constants)
   */
  getRequestTypes() {
    return REQUEST_TYPES;
  },

  /**
   * ดึงสภาพอุปกรณ์เสริมเดิม (constants)
   */
  getAccessoryConditions() {
    return ACCESSORY_CONDITIONS;
  },

  /**
   * Export ประวัติการขออุปกรณ์เสริมเป็น Excel
   */
  async exportToExcel(filters: Omit<AllHistoryFilters, 'page' | 'limit'> = {}): Promise<Blob> {
    const params: Record<string, string | number | undefined> = {};
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.company_id) params.company_id = filters.company_id;
    if (filters.request_type) params.request_type = filters.request_type;

    const response = await ApiWithLogging.get<Blob>(
      '/accessory-requests/export/excel',
      { params, responseType: 'blob' },
      {
        service: 'accessoryRequestService',
        action: 'export_accessory_requests_excel',
        entityType: 'accessory_request',
      }
    );
    return response;
  },

  /**
   * Helper: ดึง label จาก request_type
   */
  getRequestTypeLabel(type: AccessoryRequestType): string {
    const found = REQUEST_TYPES.find(t => t.value === type);
    return found?.label || type;
  },

  /**
   * Helper: ดึง color จาก request_type
   */
  getRequestTypeColor(type: AccessoryRequestType): string {
    const found = REQUEST_TYPES.find(t => t.value === type);
    return found?.color || 'gray';
  },

  /**
   * Helper: ดึง label จาก condition
   */
  getConditionLabel(condition: AccessoryCondition): string {
    const found = ACCESSORY_CONDITIONS.find(c => c.value === condition);
    return found?.label || condition;
  },
};

export default accessoryRequestService;
